// src/pages/pmc/PmcDashboard.js

import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import AgencyRegistrationsPane from "./PmcAgencyRegistrationsPane";
import PacsApplicationsPane from "./PmcPacsApplicationsPane";
import FieldVisitPane from "./PmcFieldVisitPane";
import ApprovalDeskPane from "./PmcApprovalDeskPane";
import WorkflowStageMonitorPane from "./PmcWorkflowStageMonitorPane";
import PmcBoqSetupPane from "./PmcBoqSetupPane";
import PmcEngineerMeasurementSetupPane from "./PmcEngineerMeasurementSetupPane";
import PmcFinalRaApproval from "./PmcFinalRaApproval";
import PmcDailyProgressReport from "./PmcDailyProgressReport";
import PmcEngineerApprovals from "./PmcEngineerApprovals";
import PmcRaBillsRegisterPane from "./PmcRaBillsRegisterPane";
import PmcEngineerRaBillsRegisterPane from "./PmcEngineerRaBillsRegisterPane";
import PmcLoaWorkOrderPane from "./PmcLoaWorkOrderPane";
import PmcContractorDocumentsPane from "./PmcContractorDocumentsPane";
import PmcPaymentRegisterPane from "./PmcPaymentRegisterPane";
import GenerateLOA from "./GenerateLOA";

const cx = (...xs) => xs.filter(Boolean).join(" ");

const CEO_EMAIL = "ceo@shineagri.org";
const PMC_PROFILE_COLLECTIONS = ["pmc_users", "nafed_users", "users"];
const ALLOWED_PMC_ROLES = [
  "CEO",
  "ADMIN",
  "PMC",
  "STAFF",
  "PMC_ADMIN",
  "PMC_ENGINEER",
];
const SUPER_ROLES = ["CEO", "ADMIN", "PMC", "PMC_ADMIN"];

const cleanEmail = (value) => String(value || "").trim().toLowerCase();
const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
const normalizeStatus = (value) =>
  String(value || "ACTIVE").trim().toUpperCase();

const buildCeoDashboardProfile = (user) => ({
  uid: user.uid,
  name: "Nareshbhai P Chaudhary",
  email: CEO_EMAIL,
  role: "CEO",
  status: "ACTIVE",
  isActive: true,
  modules: ["ALL"],
  permissions: ["ALL"],
  authFallback: true,
});

async function resolveDashboardProfile(user) {
  if (!user?.uid) throw new Error("AUTH USER NOT FOUND");

  const normalizedEmail = cleanEmail(user.email);
  let profile = null;

  for (const collectionName of PMC_PROFILE_COLLECTIONS) {
    try {
      const snapshot = await getDoc(doc(db, collectionName, user.uid));
      if (snapshot.exists()) {
        profile = snapshot.data() || {};
        break;
      }
    } catch (error) {
      console.warn(
        `PMC dashboard UID check skipped for ${collectionName}:`,
        error?.code || error?.message || error
      );
    }
  }

  if (!profile && normalizedEmail) {
    for (const collectionName of PMC_PROFILE_COLLECTIONS) {
      try {
        const profileQuery = query(
          collection(db, collectionName),
          where("email", "==", normalizedEmail)
        );
        const snapshot = await getDocs(profileQuery);
        if (!snapshot.empty) {
          profile = snapshot.docs[0].data() || {};
          break;
        }
      } catch (error) {
        console.warn(
          `PMC dashboard email check skipped for ${collectionName}:`,
          error?.code || error?.message || error
        );
      }
    }
  }

  if (!profile) {
    try {
      const tokenResult = await user.getIdTokenResult();
      const claimRole = normalizeRole(
        tokenResult?.claims?.role || tokenResult?.claims?.userRole
      );
      if (ALLOWED_PMC_ROLES.includes(claimRole)) {
        profile = {
          email: normalizedEmail,
          role: claimRole,
          status: "ACTIVE",
          isActive: true,
          modules: tokenResult?.claims?.modules || [],
          permissions: tokenResult?.claims?.permissions || [],
          profileSource: "firebase-custom-claims",
        };
      }
    } catch (error) {
      console.warn(
        "PMC dashboard custom-claim check skipped:",
        error?.message || error
      );
    }
  }

  // The CEO fallback is restricted to the exact authenticated Firebase email.
  // It prevents Firestore profile-rule problems from signing out the CEO.
  if (!profile && normalizedEmail === CEO_EMAIL) {
    profile = buildCeoDashboardProfile(user);
  }

  if (!profile) throw new Error("PMC PROFILE NOT FOUND");

  if (profile.email && cleanEmail(profile.email) !== normalizedEmail) {
    throw new Error("PMC EMAIL MISMATCH");
  }

  const status = normalizeStatus(profile.status);
  if (
    profile.isActive === false ||
    ["INACTIVE", "BLOCKED", "SUSPENDED", "REJECTED"].includes(status)
  ) {
    throw new Error("PMC USER INACTIVE");
  }

  let role = normalizeRole(profile.role);
  if (normalizedEmail === CEO_EMAIL) role = "CEO";
  if (!role && Array.isArray(profile.permissions)) role = "STAFF";
  if (!ALLOWED_PMC_ROLES.includes(role)) {
    throw new Error("NO DASHBOARD PERMISSION ASSIGNED");
  }

  return {
    ...profile,
    uid: user.uid,
    email: normalizedEmail,
    role,
    status,
  };
}

const MENU = [
  {
    group: "Access",
    items: [
      {
        key: "agency",
        step: "01",
        icon: "🏢",
        label: "Agency Registrations",
        desc: "Stakeholder login approvals",
      },
    ],
  },
  {
    group: "Application Flow",
    items: [
      {
        key: "pacs",
        step: "02",
        icon: "📄",
        label: "PACS Applications",
        desc: "Direct godown applications",
      },
      {
        key: "visit",
        step: "03",
        icon: "📍",
        label: "Field Visit",
        desc: "Visit planning & documents",
      },
      {
        key: "pmcapproval",
        step: "04",
        icon: "✅",
        label: "PMC Approval Desk",
        desc: "Approve to workflow",
      },
      {
        key: "workflow",
        step: "05",
        icon: "🧭",
        label: "Workflow Monitor",
        desc: "Vendor uploaded docs",
      },
    ],
  },
  {
    group: "Execution",
    items: [
      {
        key: "boq",
        step: "06",
        icon: "📊",
        label: "BOQ & RA Setup",
        desc: "Excel BOQ to tender",
      },
      {
        key: "dailyProgress",
        step: "07",
        icon: "📸",
        label: "Daily Progress",
        desc: "Site progress reports",
      },
      {
        key: "measSetup",
        step: "08",
        icon: "📐",
        label: "Measurement Template",
        desc: "Engineer sheet setup",
      },
      {
        key: "rabills",
        step: "09",
        icon: "🧾",
        label: "Contractor RA Bills",
        desc: "Contractor submitted RA",
      },
      {
        key: "ENGINEER_RA",
        step: "10",
        icon: "🧑‍💼",
        label: "Engineer RA Bills",
        desc: "Engineer verified RA",
      },
      {
        key: "finalRa",
        step: "11",
        icon: "🏁",
        label: "Final RA Approval",
        desc: "Final PMC approval",
      },
      {
        key: "loa",
        step: "12",
        icon: "📜",
        label: "LOA / Work Order",
        desc: "LOA status and signed copy",
      },
      {
        key: "contractorDocs",
        step: "13",
        icon: "📁",
        label: "Contractor Documents",
        desc: "Verify contractor uploads",
      },
      {
        key: "payments",
        step: "14",
        icon: "💰",
        label: "Payment Register",
        desc: "Mark RA payment paid",
      },
      {
        key: "generateLoa",
        step: "15",
        icon: "📄",
        label: "Generate LOA",
        desc: "Auto generate final LOA PDF",
      },
    ],
  },
  {
    group: "Master",
    items: [
      {
        key: "engineerApprovals",
        step: "16",
        icon: "👷",
        label: "Engineer Approvals",
        desc: "Engineer onboarding",
      },
    ],
  },
];

const TAB_TITLES = {
  agency: {
    title: "Agency Registration Control",
    subtitle: "Approve stakeholder logins for registrar, bank, nodal agency and office users.",
  },
  pacs: {
    title: "PACS Direct Applications",
    subtitle: "Review, edit, download PDF and permanently delete PACS godown applications.",
  },
  visit: {
    title: "Field Visit Assignment",
    subtitle: "Assign site visits, upload visit documents and mark visit status.",
  },
  pmcapproval: {
    title: "PMC Approval Desk",
    subtitle: "Approve eligible applications and create workflow projects.",
  },
  workflow: {
    title: "Workflow Stage Monitor",
    subtitle: "Track workflow stages and download vendor-uploaded stage documents.",
  },
  boq: {
    title: "BOQ & RA Setup",
    subtitle: "Upload BOQ Excel to tender so contractor can create online RA bills.",
  },
  dailyProgress: {
    title: "Daily Progress Report",
    subtitle: "Monitor contractor site progress, photos and activity logs.",
  },
  measSetup: {
    title: "Engineer Measurement Template",
    subtitle: "Upload measurement format for engineer verification.",
  },
  rabills: {
    title: "Contractor RA Bills Register",
    subtitle: "Review contractor RA bills generated from measured quantity.",
  },
  ENGINEER_RA: {
    title: "Engineer RA Bills",
    subtitle: "Review engineer verified RA bills before final approval.",
  },
  finalRa: {
    title: "Final RA Approval",
    subtitle: "Final bill approval, lock and official payment-ready records.",
  },
  loa: {
    title: "LOA / Work Order Register",
    subtitle: "Track LOA generated, contractor signed upload and PMC acceptance.",
  },
  contractorDocs: {
    title: "Contractor Documents Verification",
    subtitle: "View, approve or reject project-wise contractor uploaded documents.",
  },
  payments: {
    title: "Payment Register",
    subtitle: "Track approved RA bills, mark paid amount, UTR number and payment date.",
  },
  generateLoa: {
    title: "Generate LOA",
    subtitle: "Generate final Letter of Award and attach BOQ automatically.",
  },
  engineerApprovals: {
    title: "Engineer Approvals",
    subtitle: "Approve engineers and manage technical verification access.",
  },
};

function SidebarItem({ item, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "w-full rounded-2xl px-3 py-3 text-left transition-all border",
        active
          ? "bg-emerald-600 text-white border-emerald-500 shadow-lg shadow-emerald-900/20"
          : "bg-white/70 text-slate-700 border-white/70 hover:bg-emerald-50 hover:border-emerald-200"
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cx(
            "grid h-10 w-10 shrink-0 place-items-center rounded-xl text-lg",
            active ? "bg-white/20" : "bg-emerald-50"
          )}
        >
          {item.icon}
        </div>

        <div className="min-w-0 flex-1">
          <div
            className={cx(
              "text-[10px] font-black tracking-wider",
              active ? "text-emerald-100" : "text-emerald-600"
            )}
          >
            STEP {item.step}
          </div>

          <div
            className={cx(
              "mt-0.5 truncate text-sm font-black",
              active ? "text-white" : "text-slate-900"
            )}
          >
            {item.label}
          </div>

          <div
            className={cx(
              "mt-0.5 line-clamp-1 text-[11px]",
              active ? "text-emerald-50" : "text-slate-500"
            )}
          >
            {item.desc}
          </div>
        </div>
      </div>
    </button>
  );
}

function StatCard({ label, value, note, icon, tone = "emerald" }) {
  const tones = {
    emerald: "from-emerald-500 to-teal-600",
    blue: "from-sky-500 to-blue-600",
    amber: "from-amber-400 to-orange-500",
    purple: "from-violet-500 to-purple-600",
    red: "from-rose-500 to-red-600",
    slate: "from-slate-700 to-slate-900",
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-white/70 bg-white shadow-[0_16px_45px_rgba(15,23,42,0.08)]">
      <div className="flex items-center gap-4 p-4">
        <div
          className={cx(
            "grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br text-xl text-white shadow-lg",
            tones[tone] || tones.emerald
          )}
        >
          {icon}
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-black uppercase tracking-wider text-slate-500">
            {label}
          </div>
          <div className="mt-1 text-2xl font-black text-slate-950">{value}</div>
          {note && (
            <div className="mt-0.5 truncate text-[11px] font-medium text-slate-400">
              {note}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FlowPill({ index, label, active }) {
  return (
    <div
      className={cx(
        "flex items-center gap-2 rounded-full border px-3 py-2 text-[11px] font-black",
        active
          ? "border-emerald-400 bg-emerald-600 text-white shadow"
          : "border-slate-200 bg-white text-slate-500"
      )}
    >
      <span
        className={cx(
          "grid h-5 w-5 place-items-center rounded-full text-[10px]",
          active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
        )}
      >
        {index}
      </span>
      {label}
    </div>
  );
}

function LoadingBox() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-sm font-semibold text-slate-500">
      Loading PMC summary...
    </div>
  );
}

export default function PmcDashboard() {
  const [tab, setTab] = useState("");
  const [dark, setDark] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  const [summary, setSummary] = useState({
    loading: true,
    applications: 0,
    pendingVisit: 0,
    pendingApproval: 0,
    workflowProjects: 0,
    activeTenders: 0,
    pendingRa: 0,
    engineerRa: 0,
    finalRa: 0,
    signedLoaPending: 0,
    contractorDocsPending: 0,
    paymentPending: 0,
    paidRaAmount: 0,
    engineers: 0,
  });

  const isSuperUser = SUPER_ROLES.includes(normalizeRole(userProfile?.role));

  const canAccess = (key) => {
    if (!userProfile) return false;
    if (isSuperUser) return true;
    const grants = [
      ...(Array.isArray(userProfile.permissions)
        ? userProfile.permissions
        : []),
      ...(Array.isArray(userProfile.modules) ? userProfile.modules : []),
    ].map((value) => String(value || "").trim());
    return grants.includes("ALL") || grants.includes(key);
  };

  const allowedMenu = useMemo(() => {
    if (!userProfile) return [];

    return MENU.map((group) => ({
      ...group,
      items: (Array.isArray(group.items) ? group.items : []).filter((item) =>
        canAccess(item.key)
      ),
    })).filter((group) => group.items.length > 0);
  }, [userProfile]);

  const allAllowedMenuItems = useMemo(
    () => allowedMenu.flatMap((g) => (Array.isArray(g.items) ? g.items : [])),
    [allowedMenu]
  );

  const activeMeta = TAB_TITLES[tab] || {
    title: "No Module Assigned",
    subtitle: "Please contact admin to assign dashboard permission.",
  };

  const activeIndex = useMemo(() => {
    const idx = MENU.flatMap((g) => g.items).findIndex((x) => x.key === tab);
    return idx >= 0 ? idx + 1 : 1;
  }, [tab]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      try {
        if (!u) {
          localStorage.removeItem("pmcUser");
          window.location.replace("/pmc/login");
          return;
        }

        const profile = await resolveDashboardProfile(u);
        localStorage.setItem(
          "pmcUser",
          JSON.stringify({ ...profile, sessionValidatedAt: Date.now() })
        );
        setUserProfile(profile);

        setAuthLoading(false);
      } catch (err) {
        console.error("PMC DASHBOARD AUTH ERROR:", {
          code: err?.code || "PMC_DASHBOARD_AUTH_FAILED",
          message: err?.message || String(err),
        });
        localStorage.removeItem("pmcUser");
        toast.error(
          err?.message?.includes("INACTIVE")
            ? "Account inactive."
            : err?.message?.includes("PERMISSION")
            ? "No dashboard permission assigned."
            : "PMC permission profile not found."
        );
        if (auth.currentUser?.uid === u?.uid) await signOut(auth);
        window.location.replace("/pmc/login");
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!userProfile) return;

    const firstItem = allAllowedMenuItems?.[0];

    if (!firstItem) {
      setTab("");
      return;
    }

    if (!tab || !canAccess(tab)) {
      setTab(firstItem.key);
    }
  }, [userProfile, allAllowedMenuItems, tab]);

  const loadSummary = async () => {
    try {
      setSummary((p) => ({ ...p, loading: true }));

      const [
        godownSnap,
        wfSnap,
        tenderSnap,
        raSnap,
        engineerRaSnap,
        finalRaSnap,
        engineersSnap,
        contractorDocsSnap,
      ] = await Promise.all([
        getDocs(collection(db, "godown_projects")),
        getDocs(collection(db, "wf_projects")),
        getDocs(collection(db, "tenders")),
        getDocs(collection(db, "ra_bills")),
        getDocs(collection(db, "ra_bills_engineer")),
        getDocs(collection(db, "ra_bills_final")),
        getDocs(collection(db, "engineers")),
        getDocs(collection(db, "contractor_documents")),
      ]);

      const godowns = godownSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const tenders = tenderSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const ra = raSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const engineerRa = engineerRaSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const finalRa = finalRaSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const contractorDocs = contractorDocsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setSummary({
        loading: false,
        applications: godowns.length,
        pendingVisit: godowns.filter((x) => !x.siteVisitStatus || x.siteVisitStatus === "NEW").length,
        pendingApproval: godowns.filter((x) => (x.pmcDecision || "PENDING") === "PENDING").length,
        workflowProjects: wfSnap.size,
        activeTenders: tenders.filter((x) => x.awardedVendorUid).length,
        pendingRa: ra.filter((x) => (x.status || "pending") === "pending").length,
        engineerRa: engineerRa.filter((x) => (x.status || "pending") === "pending").length,
        finalRa: finalRa.filter((x) => (x.status || "pending") === "pending").length,
        signedLoaPending: tenders.filter((x) => x.signedLoaUrl && (x.loaPmcStatus || "PENDING") === "PENDING").length,
        contractorDocsPending: contractorDocs.filter((x) => (x.pmcStatus || "PENDING_VERIFICATION") === "PENDING_VERIFICATION").length,
        paymentPending: ra.filter((x) => (x.status || "").toLowerCase() === "approved" && (x.paymentStatus || "Pending") !== "Paid").length,
        paidRaAmount: ra
          .filter((x) => (x.paymentStatus || "").toLowerCase() === "paid")
          .reduce((s, x) => s + Number(x.paidAmount || x.totalAmount || 0), 0),
        engineers: engineersSnap.size,
      });
    } catch (e) {
  console.error("SUMMARY ERROR:", e);
  console.error("ERROR CODE:", e.code);
  console.error("ERROR MESSAGE:", e.message);

  setSummary((p) => ({ ...p, loading: false }));
  toast.error(e.code || "Summary load failed.");
}
  };

  useEffect(() => {
    if (!authLoading && userProfile) {
      loadSummary();
    }
  }, [authLoading, userProfile]);

  const doLogout = async () => {
    await signOut(auth);
    toast("Logged out");
    window.location.href = "/pmc/login";
  };

  const renderPane = () => {
    if (!tab || !canAccess(tab)) {
      return (
        <div className="rounded-3xl border border-red-100 bg-red-50 p-6 text-sm font-bold text-red-700">
          You do not have permission for this module.
        </div>
      );
    }

    if (tab === "agency") return <AgencyRegistrationsPane />;
    if (tab === "pacs") return <PacsApplicationsPane />;
    if (tab === "visit") return <FieldVisitPane />;
    if (tab === "pmcapproval") return <ApprovalDeskPane />;
    if (tab === "workflow") return <WorkflowStageMonitorPane />;
    if (tab === "boq") return <PmcBoqSetupPane />;
    if (tab === "dailyProgress") return <PmcDailyProgressReport />;
    if (tab === "measSetup") return <PmcEngineerMeasurementSetupPane />;
    if (tab === "rabills") return <PmcRaBillsRegisterPane />;
    if (tab === "ENGINEER_RA") return <PmcEngineerRaBillsRegisterPane />;
    if (tab === "finalRa") return <PmcFinalRaApproval />;
    if (tab === "loa") return <PmcLoaWorkOrderPane />;
    if (tab === "contractorDocs") return <PmcContractorDocumentsPane />;
    if (tab === "payments") return <PmcPaymentRegisterPane />;
    if (tab === "generateLoa") return <GenerateLOA />;
    if (tab === "engineerApprovals") return <PmcEngineerApprovals />;

    return (
      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 text-sm font-bold text-slate-600">
        Module not found.
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-emerald-50 flex items-center justify-center">
        <div className="rounded-2xl bg-white border border-emerald-200 px-6 py-4 text-sm font-bold text-emerald-800 shadow">
          Checking dashboard permission...
        </div>
      </div>
    );
  }

  if (!allAllowedMenuItems.length) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen bg-emerald-50 flex items-center justify-center px-4">
          <div className="max-w-md rounded-3xl bg-white border border-red-100 p-6 shadow text-center">
            <div className="text-lg font-black text-red-700">No Permission Assigned</div>
            <p className="mt-2 text-sm text-slate-600">
              Is user ko koi dashboard module permission nahi mili hai.
            </p>
            <button
              onClick={doLogout}
              className="mt-4 rounded-full bg-slate-950 px-5 py-2 text-xs font-black text-white"
            >
              Logout
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />

      <div
        className={cx(
          "min-h-screen",
          dark
            ? "bg-slate-950 text-white"
            : "bg-gradient-to-br from-emerald-50 via-slate-50 to-sky-50 text-slate-900"
        )}
      >
        <div className="sticky top-0 z-30 border-b border-white/60 bg-white/80 backdrop-blur-xl shadow-sm">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4 px-5 py-4">
            <div className="flex items-center gap-4">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 text-lg font-black text-white shadow-lg">
                PMC
              </div>

              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-700">
                  Shine Agri Project Management Console
                </div>

                <h1 className="text-xl font-black tracking-tight text-slate-950 md:text-2xl">
                  PMC Master Dashboard
                </h1>

                <p className="text-[12px] font-medium text-slate-500">
                  Logged in as: {userProfile?.name || userProfile?.email || "User"}{" "}
                  | Role: {userProfile?.role || "STAFF"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={loadSummary}
                className="rounded-full border border-emerald-200 bg-white px-4 py-2 text-xs font-black text-emerald-700 shadow-sm hover:bg-emerald-50"
              >
                Refresh
              </button>

              <button
                type="button"
                onClick={() => setDark((d) => !d)}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-700 shadow-sm hover:bg-slate-50"
              >
                {dark ? "Light" : "Dark"}
              </button>

              <button
                type="button"
                onClick={doLogout}
                className="rounded-full bg-slate-950 px-4 py-2 text-xs font-black text-white shadow hover:bg-slate-800"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        <div className="mx-auto flex max-w-[1600px] gap-5 px-5 py-5">
          <aside className="sticky top-[92px] hidden h-[calc(100vh-110px)] w-[310px] shrink-0 overflow-y-auto rounded-[2rem] border border-white/70 bg-white/80 p-4 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl lg:block">
            <div className="mb-4 rounded-3xl bg-slate-950 p-4 text-white">
              <div className="text-[11px] font-black uppercase tracking-[0.2em] text-emerald-300">
                Control Flow
              </div>
              <div className="mt-2 text-lg font-black">PMC Operations</div>
              <div className="mt-1 text-[12px] leading-5 text-slate-300">
                Only assigned modules are visible here.
              </div>
            </div>

            <div className="space-y-5">
              {allowedMenu.map((group) => (
                <div key={group.group}>
                  <div className="mb-2 px-2 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                    {group.group}
                  </div>

                  <div className="space-y-2">
                    {group.items.map((item) => (
                      <SidebarItem
                        key={item.key}
                        item={item}
                        active={tab === item.key}
                        onClick={() => setTab(item.key)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <main className="min-w-0 flex-1 space-y-5">
            <div className="lg:hidden rounded-3xl border border-white/70 bg-white p-4 shadow">
              <div className="text-[11px] font-black uppercase tracking-wider text-slate-500">
                Select Module
              </div>

              <select
                value={tab}
                onChange={(e) => setTab(e.target.value)}
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none focus:border-emerald-400"
              >
                {allowedMenu.flatMap((g) =>
                  g.items.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.step}) {item.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-white shadow-[0_20px_70px_rgba(15,23,42,0.08)]">
              <div className="relative bg-slate-950 px-6 py-6 text-white">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.40),transparent_35%),radial-gradient(circle_at_top_right,rgba(56,189,248,0.25),transparent_30%)]" />

                <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[0.2em] text-emerald-300">
                      Active Module
                    </div>

                    <h2 className="mt-2 text-2xl font-black tracking-tight md:text-3xl">
                      {activeMeta.title}
                    </h2>

                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
                      {activeMeta.subtitle}
                    </p>
                  </div>

                  <div className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                    <div className="text-[11px] font-black uppercase tracking-wider text-emerald-200">
                      Current Step
                    </div>

                    <div className="mt-1 text-4xl font-black">
                      {String(activeIndex).padStart(2, "0")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto border-t border-slate-100 bg-slate-50 px-5 py-4">
                <div className="flex min-w-max gap-2">
                  {allAllowedMenuItems.map((item, idx) => (
                    <FlowPill
                      key={item.key}
                      index={idx + 1}
                      label={item.label}
                      active={tab === item.key}
                    />
                  ))}
                </div>
              </div>
            </section>

            {summary.loading ? (
              <LoadingBox />
            ) : (
              <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <StatCard label="Applications" value={summary.applications} note="Total godown applications" icon="📄" tone="emerald" />
                <StatCard label="Pending Approval" value={summary.pendingApproval} note="PMC approval pending" icon="✅" tone="amber" />
                <StatCard label="Awarded Tenders" value={summary.activeTenders} note="Tender awarded to contractor" icon="🏗️" tone="slate" />
                <StatCard label="Contractor RA" value={summary.pendingRa} note="Pending contractor RA bills" icon="🧾" tone="red" />
                <StatCard label="Signed LOA Pending" value={summary.signedLoaPending} note="Contractor signed copy pending acceptance" icon="📜" tone="purple" />
                <StatCard label="Docs Pending" value={summary.contractorDocsPending} note="Contractor document verification" icon="📁" tone="blue" />
                <StatCard label="Payment Pending" value={summary.paymentPending} note="Approved RA payment pending" icon="💰" tone="amber" />
                <StatCard label="Paid RA Amount" value={`₹ ${Number(summary.paidRaAmount || 0).toLocaleString("en-IN")}`} note="Total processed payments" icon="✅" tone="emerald" />
              </section>
            )}

            <section className="rounded-[2rem] border border-white/70 bg-white p-4 shadow-[0_20px_70px_rgba(15,23,42,0.08)] md:p-5">
              <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-600">
                    Module Workspace
                  </div>

                  <div className="mt-1 text-lg font-black text-slate-950">
                    {activeMeta.title}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={loadSummary}
                  className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-black text-slate-600 hover:bg-slate-100"
                >
                  Refresh Counts
                </button>
              </div>

              <div>{renderPane()}</div>
            </section>
          </main>
        </div>
      </div>
    </>
  );
}
